import React, { useEffect, useState, useRef, useCallback } from "react";
import { PiNotepad } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import debounce from "lodash/debounce";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";

import CustomModel from "../../components/common/CustomModel";
import employeeAnalyticsService from "../Services/employeeAnalyticsService";
import {
  showErrorToast,
  showSuccessToast,
} from "../../components/Constant/Notification";
import { userUid } from "../../utils/storage";
import { formatToIndianDate } from "../../utils/dateFormatter";
import leaveRequestService from "../Services/leaveRequestService";
import CustomSearch from "../../components/common/CustomSearch";
import { convertTo24Hour } from "../../utils/timeFormate";
import { mapLoaderConfig } from "../../GeoLocationCompoents/Services/mapLoaderConfig";

const AttendanceEmployeeList = ({ selectedTeam, selectedDate }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [isPresent, setIsPresent] = useState(false);
  const [isAbsent, setIsAbsent] = useState(false);
  const [isLeave, setIsLeave] = useState(false);
  const [isOvertime, setIsOvertime] = useState(false);
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [isFine, setIsFine] = useState(false);
  const [employeeData, setEmployeeData] = useState([]);
    const { isLoaded } = useJsApiLoader(mapLoaderConfig);

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [nextId, setNextId] = useState("");
  const [prevId, setPrevId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentSearchTerm, setCurrentSearchTerm] = useState("");

  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [fineAmount, setFineAmount] = useState("");
  const [loginTime, setLoginTime] = useState("");
  const [logoutTime, setLogoutTime] = useState("");
  const [overtimeLoginTime, setOvertimeLoginTime] = useState("");
  const [overtimeLogoutTime, setOvertimeLogoutTime] = useState("");
  const [loading, setLoading] = useState(false);

  const [requestCount, setRequestCount] = useState(0);
  const [isLoginTime, setIsLoginTime] = useState(false);

  const [halfDayTime, setHalfDayTime] = useState("");

  // Add missing state variables for image modal
  const [isImageModalVisible, setIsImageModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedCoordinates, setSelectedCoordinates] = useState(null);
  const [selectedType, setSelectedType] = useState("");
  const [currentLocationAddress, setCurrentLocationAddress] = useState("Loading...");
  const [imageLoading, setImageLoading] = useState(false);

  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const observerRef = useRef(null);
  const debouncedSearchRef = useRef();
  const dataCache = useRef({});
  const loadingRef = useRef(false);
  const allLoadedData = useRef([]); // Store all loaded data for current search/filter

  // Clear cache when search term or team changes
  const clearCache = useCallback(() => {
    const previousCacheKeys = Object.keys(dataCache.current);
    const previousDataLength = allLoadedData.current.length;
    
    dataCache.current = {};
    allLoadedData.current = [];
    
    console.log("Cache cleared", { 
      previousCacheKeys, 
      previousDataLength,
      reason: "Search/Team/Date change"
    });
  }, []);

  // Refresh data after operations
  const refreshData = useCallback(() => {
    clearCache();
    allLoadedData.current = [];
    setEmployeeData([]);
    setNextId("");
    setPrevId("");
    setHasMore(true);
  }, [clearCache]);

  useEffect(() => {
    // Clear cache when search term, selected team, or selected date changes
    clearCache();
  }, [currentSearchTerm, selectedTeam, selectedDate, clearCache]);

  useEffect(() => {
    debouncedSearchRef.current = debounce((value) => {
      setCurrentSearchTerm(value);
      clearCache(); // Clear cache when search term changes
    }, 500);

    return () => {
      if (debouncedSearchRef.current) {
        debouncedSearchRef.current.cancel();
      }
    };
  }, [selectedTeam, clearCache]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (debouncedSearchRef.current) {
      debouncedSearchRef.current(value);
    }
  };

  const fetchRequestCount = async () => {
    try {
      const response = await leaveRequestService.leaveRequestCount({
        fromDate: formatToIndianDate(selectedDate), 
        toDate: formatToIndianDate(selectedDate),
      });
      if (response && response.data) {
        setRequestCount(response.data.count || 0);
      }
    } catch (error) {
      console.error("Error fetching leave request count:", error);
      return 0;
    }
  };

  useEffect(() => {
    fetchRequestCount();
  }, []);

  const fetchEmployeeData = useCallback(
    async (
      searchKey = currentSearchTerm,
      teamId = selectedTeam?.value || "",
      pageToken = "",
      direction = "next",
      reset = false
    ) => {
      if (loadingRef.current) return;
      if (!reset && !hasMore && direction === "next") return;

      const cacheKey = `${searchKey}-${teamId}-${pageToken}-${direction}`;

      // Check if we already have this data in cache
      if (!reset && dataCache.current[cacheKey]) {
        console.log("Using cached data for:", cacheKey, {
          cachedEmployees: dataCache.current[cacheKey].employees.length,
          currentAllLoadedData: allLoadedData.current.length,
          direction
        });
        const cachedData = dataCache.current[cacheKey];
        
          // For next direction, append cached data to existing data
          if (direction === "next") {
            const existingIds = new Set(allLoadedData.current.map(emp => emp.user.value));
            const newEmployees = cachedData.employees.filter(emp => !existingIds.has(emp.user.value));
            
            console.log("New employees from cache:", newEmployees.length);
            
            if (newEmployees.length > 0) {
              allLoadedData.current = [...allLoadedData.current, ...newEmployees];
              setEmployeeData(allLoadedData.current);
              console.log("Updated allLoadedData length:", allLoadedData.current.length);
            }
          } else {
            setEmployeeData(cachedData.employees);
          }        setNextId(cachedData.next || "");
        setPrevId(cachedData.prev || "");
        setHasMore(!!cachedData.next);
        return;
      }

      try {
        loadingRef.current = true;
        setIsLoading(true);

        if (reset) {
          setEmployeeData([]);
          setNextId("");
          setPrevId("");
          setHasMore(true);
          allLoadedData.current = [];
        }

        const body = {
          date: selectedDate,
          searchKey: searchKey.trim(),
          teamId,
          limit: 10,
          next: direction === "next" ? pageToken : "",
          prev: direction === "prev" ? pageToken : "",
          direction,
        };

        console.log("Making API call for:", cacheKey);
        const response =
          await employeeAnalyticsService.getEmployeeAnalyticsList(body);
        const newData = response.data.employees || [];

        const newNext = response.data.next || "";
        const newPrev = response.data.prev || "";

        // Cache the response
        dataCache.current[cacheKey] = {
          employees: newData,
          next: newNext,
          prev: newPrev,
        };
        
        console.log("Cached data for:", cacheKey, { 
          employeeCount: newData.length, 
          next: newNext,
          employeeIds: newData.map(emp => emp.user.value)
        });

        if (reset) {
          allLoadedData.current = [...newData];
          setEmployeeData(newData);
        } else if (direction === "next") {
          // Add new data to allLoadedData
          const existingIds = new Set(allLoadedData.current.map((emp) => emp.user.value));
          const uniqueNewData = newData.filter((emp) => !existingIds.has(emp.user.value));
          allLoadedData.current = [...allLoadedData.current, ...uniqueNewData];
          
          // Update displayed data
          setEmployeeData(allLoadedData.current);
        } else {
          setEmployeeData(newData);
        }

        setNextId(newNext);
        setPrevId(newPrev);
        setHasMore(!!newNext);

        console.log("Employee data fetched successfully:", {
          count: newData.length,
          hasMore: !!newNext,
          nextId: newNext,
          prevId: newPrev,
          totalLoadedData: allLoadedData.current.length,
          displayedData: reset ? newData.length : allLoadedData.current.length
        });
      } catch (error) {
        console.error("Error fetching employee data:", error);
        setHasMore(false);
      } finally {
        setIsLoading(false);
        loadingRef.current = false;
      }
    },
    [currentSearchTerm, selectedTeam, hasMore, selectedDate]
  );

  useEffect(() => {
    fetchEmployeeData(
      currentSearchTerm,
      selectedTeam?.value || "",
      "",
      "next",
      true
    );
  }, [currentSearchTerm, selectedTeam, selectedDate]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !isLoading && nextId) {
          console.log("Loading more data...", { 
            nextId, 
            hasMore, 
            currentDataLength: employeeData.length,
            allLoadedDataLength: allLoadedData.current.length
          });
          fetchEmployeeData(
            currentSearchTerm,
            selectedTeam?.value || "",
            nextId,
            "next",
            false
          );
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: "100px",
        threshold: 0.1,
      }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [hasMore, isLoading, nextId, currentSearchTerm, selectedTeam, employeeData.length]);

  const handleInfoClick = (employee) => {
    const employeeStatus = employee.isAbsent
      ? "Absent"
      : employee.loginTime
      ? "Present"
      : employee.isLeave
      ? "Leave"
      : "Not Logged In";
    navigate(`/hr-employee-analytics/user-info`, {
      state: {
        employeeId: employee.user.value,
        employeeName: employee.user.label,
        employeeStatus: employeeStatus,
        selectedDate: selectedDate,
      },
    });
  };

  const handleManageNotes = async () => {
    const body = {
      userId: selectedEmployee.user.value,
      content: notes.trim(),
      createdBy: userUid,
      modifiedBy: userUid,
      date: selectedDate,
    };
    setLoading(true);

    try {
      const res = await employeeAnalyticsService.createEmployeeNotes(body);

      if (res.status === 201 || res.status === 200) {
        setNotes("");
        refreshData();
        // Trigger fresh data fetch
        setTimeout(() => {
          fetchEmployeeData(
            currentSearchTerm,
            selectedTeam?.value || "",
            "",
            "next",
            true
          );
        }, 100);
        setIsNotesOpen(false);
        setSelectedEmployee(null);
        showSuccessToast(res.data.message);
      }
    } catch (error) {
      console.error("Error adding notes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleManageFine = async () => {
    if (selectedEmployee?.isLeave) {
      showErrorToast("Employee is on leave");
      return;
    }

    if (selectedEmployee?.isAbsent) {
      showErrorToast("Employee is marked absent");
      return;
    }

    const body = {
      userId: selectedEmployee.user.value,
      fineAmount: Number(fineAmount),
      createdBy: userUid,
      modifiedBy: userUid,
      date: selectedDate,
    };

    if (!fineAmount || isNaN(Number(fineAmount)) || Number(fineAmount) <= 0) {
      showErrorToast("Please enter a valid fine amount");
      return;
    }
    setLoading(true);

    try {
      const res = await employeeAnalyticsService.createEmployeeFine(body);

      if (res.status === 200 || res.status === 201) {
        setFineAmount("");
        refreshData();
        // Trigger fresh data fetch
        setTimeout(() => {
          fetchEmployeeData(
            currentSearchTerm,
            selectedTeam?.value || "",
            "",
            "next",
            true
          );
        }, 100);
        setIsFine(false);
        setSelectedEmployee(null);
        showSuccessToast(res.data.message);
      }
    } catch (error) {
      console.error("Error adding fine:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleManageAbsent = async () => {
    if (selectedEmployee?.isLeave) {
      showErrorToast("Employee is already on leave");
      return;
    }

    if (selectedEmployee?.isAbsent) {
      showErrorToast("Employee is already marked absent");
      return;
    }

    const body = {
      userId: selectedEmployee.user.value,
      date: selectedDate,
      createdBy: userUid,
      modifiedBy: userUid,
    };
    setLoading(true);

    try {
      const res = await employeeAnalyticsService.createEmployeeAbsence(body);

      if (res.status === 200 || res.status === 201) {
        refreshData();
        // Trigger fresh data fetch
        setTimeout(() => {
          fetchEmployeeData(
            currentSearchTerm,
            selectedTeam?.value || "",
            "",
            "next",
            true
          );
        }, 100);
        setIsAbsent(false);
        setSelectedEmployee(null);
        showSuccessToast(res.data.message);
      }
    } catch (error) {
      console.error("Error marking absent:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleManageLeave = async () => {
    if (selectedEmployee?.isAbsent) {
      showErrorToast("Employee is already marked absent");
      return;
    }

    if (selectedEmployee?.isLeave) {
      showErrorToast("Employee is already on leave");
      return;
    }

    if (!reason.trim()) {
      showErrorToast("Please enter a reason for leave");
      return;
    }

    const body = {
      userId: selectedEmployee.user.value,
      date: selectedDate,
      reason: reason.trim(),
      createdBy: userUid,
      modifiedBy: userUid,
    };
    setLoading(true);

    try {
      const res = await employeeAnalyticsService.createEmployeeLeave(body);

      if (res.status === 200 || res.status === 201) {
        setReason("");
        refreshData();
        // Trigger fresh data fetch
        setTimeout(() => {
          fetchEmployeeData(
            currentSearchTerm,
            selectedTeam?.value || "",
            "",
            "next",
            true
          );
        }, 100);
        setIsLeave(false);
        setSelectedEmployee(null);
        showSuccessToast(res.data.message);
      }
    } catch (error) {
      console.error("Error marking leave:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleManageHalfDay = async () => {
    if (selectedEmployee?.isLeave) {
      showErrorToast("Employee is on leave");
      return;
    }

    if (selectedEmployee?.isAbsent) {
      showErrorToast("Employee is marked absent");
      return;
    }

    if (!halfDayTime) {
      showErrorToast("Please enter logout time for half day");
      return;
    }

    const body = {
      userId: selectedEmployee.user.value,
      date: selectedDate,
      time: halfDayTime,
      createdBy: userUid,
      modifiedBy: userUid,
    };
    setLoading(true);

    try {
      const res = await employeeAnalyticsService.createEmployeeHalfDay(body);

      if (res.status === 200 || res.status === 201) {
        refreshData();
        // Trigger fresh data fetch
        setTimeout(() => {
          fetchEmployeeData(
            currentSearchTerm,
            selectedTeam?.value || "",
            "",
            "next",
            true
          );
        }, 100);
        setIsHalfDay(false);
        setSelectedEmployee(null);
        setHalfDayTime("");
        showSuccessToast(res.data.message);
      }
    } catch (error) {
      console.error("Error marking half day:", error);
    } finally {
      setLoading(false);
    }
  };

  console.log("selectedEmployee", selectedEmployee);

  const handleManageOvertime = async () => {
    if (!selectedEmployee?.loginTime) {
      showErrorToast("Employee has not logged in yet");
      return;
    }

    if (selectedEmployee?.isLeave) {
      showErrorToast("Employee is on leave");
      return;
    }

    if (selectedEmployee?.isAbsent) {
      showErrorToast("Employee is marked absent");
      return;
    }

    if (!overtimeLoginTime || !overtimeLogoutTime) {
      showErrorToast("Please enter both login and logout times for overtime");
      return;
    }

    const login = new Date(`1970-01-01T${overtimeLoginTime}:00`);
    const logout = new Date(`1970-01-01T${overtimeLogoutTime}:00`);
    if (logout <= login) {
      showErrorToast("Overtime logout time must be after login time");
      return;
    }

    const body = {
      userId: selectedEmployee.user.value,
      fromTime: overtimeLoginTime,
      toTime: overtimeLogoutTime,
      fromDate: selectedDate,
      toDate: selectedDate,
      createdBy: userUid,
      modifiedBy: userUid,
    };

    setLoading(true);

    try {
      const res = await employeeAnalyticsService.createEmployeeOvertime(body);

      if (res.status === 200 || res.status === 201) {
        setOvertimeLoginTime("");
        setOvertimeLogoutTime("");
        refreshData();
        // Trigger fresh data fetch
        setTimeout(() => {
          fetchEmployeeData(
            currentSearchTerm,
            selectedTeam?.value || "",
            "",
            "next",
            true
          );
        }, 100);
        setIsOvertime(false);
        setSelectedEmployee(null);
        showSuccessToast(res.data.message);
      }
    } catch (error) {
      console.error("Error marking overtime:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleManagePresent = async () => {
    if (selectedEmployee?.isLeave) {
      showErrorToast("Employee is on leave");
      return;
    }

    if (selectedEmployee?.isAbsent) {
      showErrorToast("Employee is marked absent");
      return;
    }

    if (isLoginTime) {
      if (!logoutTime) {
        showErrorToast("Please enter logout time");
        return;
      }
    } else {
      if (!loginTime) {
        showErrorToast("Please enter login time");
        return;
      }
    }

    if (loginTime && logoutTime) {
      const login = new Date(`1970-01-01T${loginTime}:00`);
      const logout = new Date(`1970-01-01T${logoutTime}:00`);

      if (logout <= login) {
        showErrorToast("Logout time must be after login time");
        return;
      }
    }

    const body = {
      userId: selectedEmployee.user.value,
      time: isLoginTime ? logoutTime : loginTime,
      date: selectedDate,
      type: isLoginTime ? "logout" : "login",
      createdBy: userUid,
      modifiedBy: userUid,
    };

    setLoading(true);

    try {
      const res = await employeeAnalyticsService.updateEmployeePresent(body);

      if (res.status === 200 || res.status === 201) {
        setLoginTime("");
        setLogoutTime("");
        refreshData();
        // Trigger fresh data fetch
        setTimeout(() => {
          fetchEmployeeData(
            currentSearchTerm,
            selectedTeam?.value || "",
            "",
            "next",
            true
          );
        }, 100);
        setIsPresent(false);
        setSelectedEmployee(null);
        showSuccessToast(res.data.message);
      }
    } catch (error) {
      console.error("Error updating present status:", error);
      showErrorToast(error?.data?.error || "Error updating present status");
    } finally {
      setLoading(false);
    }
  };

  // Function to get address from coordinates using reverse geocoding
  const getAddressFromCoordinates = async (lat, lng) => {
    try {
      const geocoder = new window.google.maps.Geocoder();
      const result = await new Promise((resolve, reject) => {
        geocoder.geocode(
          { location: { lat: parseFloat(lat), lng: parseFloat(lng) } },
          (results, status) => {
            if (status === "OK" && results[0]) {
              resolve(results[0].formatted_address);
            } else {
              reject(new Error("Geocoding failed"));
            }
          }
        );
      });
      return result;
    } catch (error) {
      console.error("Error getting address:", error);
      return "Address not available";
    }
  };

  // Function to handle image click and show location
  const handleImageClick = async (imageUrl, lat, lng, type) => {
    if (!imageUrl || !lat || !lng) {
      showErrorToast("Location data not available for this image");
      return;
    }

    setSelectedImage(imageUrl);
    setSelectedCoordinates(`${lat},${lng}`);
    setSelectedType(type);
    setIsImageModalVisible(true);
    setCurrentLocationAddress("Loading...");
    
    // Get address from coordinates
    if (isLoaded) {
      try {
        const address = await getAddressFromCoordinates(lat, lng);
        setCurrentLocationAddress(address);
      } catch (error) {
        setCurrentLocationAddress("Address not available");
      }
    }
  };

  return (
    <div className="">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <CustomSearch
          placeholder="Search Employee Name"
          handleSearchChange={handleSearchChange}
          searchTerm={searchTerm}
          handleSearchSubmit={(e) => {
            e.preventDefault();
            setCurrentSearchTerm(searchTerm);
            clearCache();
            fetchEmployeeData(
              searchTerm,
              selectedTeam?.value || "",
              "",
              "next",
              true
            );
          }}
        />
        <div
          onClick={() => navigate("/hr-employee-analytics/request-list")}
          className="text-sm text-gray-600 cursor-pointer bg-white p-3 rounded-lg"
        >
          Leave Request ({requestCount})
        </div>
      </div>

      {/* Employee List */}
      <div
        ref={scrollContainerRef}
        style={{ maxHeight: "calc(100vh - 90px)" }}
        className="flex-1 overflow-y-auto min-h-0"
      >
        <div className="divide-y divide-gray-100">
          {employeeData.length > 0 ? (
            employeeData.map((employee) => (
              <div
                key={employee.user.value}
                className="p-6 bg-white mb-4 rounded-lg transition-colors"
              >
                <div className="flex items-start justify-between gap-8">
                  {/* Employee Info - Left Side */}
                  <div className="flex-1 max-w-md">
                    <div className="flex items-center gap-3 mb-3">
                      <h3
                        onClick={() => handleInfoClick(employee)}
                        className="font-semibold text-wrap truncate line-clamp-2 cursor-pointer hover:text-customGreen text-gray-900 text-lg"
                      >
                        {employee.user.label}
                      </h3>
                      <span
                        className={`px-3 py-1 text-nowrap rounded-full border text-xs font-medium ${
                          employee.loginTime
                            ? "bg-blue-100 text-blue-800 border-blue-800"
                            : "bg-red-100 text-red-800 border-red-800"
                        }`}
                      >
                        {employee.loginTime
                          ? `Logged In: ${employee.loginTime}`
                          : "Not Logged In"}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mb-2">
                      Team: {employee.teams?.[0]?.label || "No Team Assigned"}
                      <div className="text-blue-500">
                        {employee.teams && employee.teams.length > 1
                          ? `, ${employee.teams.length - 1}+`
                          : ""}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 mb-3">
                      <div className="flex flex-col">
                        <span>
                          Weekdays Shift: {employee?.shiftTimings?.weekday || "Not Set"}
                        </span>
                        <span>
                          Weekend Shift: {employee?.shiftTimings?.weekend || "Not Set"}
                        </span>
                      </div>
                    </div>
                    <div
                      onClick={() => {
                        setSelectedEmployee(employee);
                        setIsNotesOpen(true);
                        setNotes(employee.notes?.content || "");
                      }}
                      className="flex items-center gap-2 bg-slate-50 hover:bg-slate-100 rounded-2xl p-2 w-fit text-orange-500 cursor-pointer hover:text-orange-600"
                    >
                      <PiNotepad className="text-lg" />
                      <span className="text-sm font-medium">
                        {employee.notes?.content
                          ? "View/Edit Notes"
                          : "Add Logs Notes"}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 min-w-fit">
                    {/* Row 1 */}
                    <div
                      onClick={() => {
                        if (employee.isLeave || employee.isAbsent) return;
                        setIsPresent(true);
                        setSelectedEmployee(employee);
                        setLoginTime(convertTo24Hour(employee.loginTime) || "");
                        setLogoutTime(
                          convertTo24Hour(employee.logoutTime) || ""
                        );
                        setIsLoginTime(employee.loginTime || false);
                      }}
                      className={`${
                        employee.loginHours || employee.loginTime
                          ? "bg-teal-100 text-teal-800"
                          : employee.isLeave || employee.isAbsent
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-gray-100 text-gray-700 cursor-pointer"
                      } px-4 w-[200px] py-3 rounded-lg text-sm font-medium whitespace-nowrap`}
                    >
                      P |{" "}
                      {employee.loginHours
                        ? employee.loginHours
                        : employee.loginTime || "Not Logged In"}
                    </div>
                    <div
                      onClick={() => {
                        if (employee.isLeave || employee.isAbsent) return;
                        setIsHalfDay(true);
                        setSelectedEmployee(employee);
                        setHalfDayTime(employee.halfDayInDetail || "");
                      }}
                      className={`${
                        employee.halfDay
                          ? "bg-blue-100 text-blue-800"
                          : employee.isLeave || employee.isAbsent
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-gray-100 text-gray-700 cursor-pointer"
                      } px-4 py-3 w-[200px] rounded-lg text-sm font-medium whitespace-nowrap`}
                    >
                      HD | {employee.halfDay || "No Half Day"}
                    </div>
                    <div
                      onClick={() => {
                        if (employee.isLeave || employee.isAbsent) return;
                        setIsAbsent(true);
                        setSelectedEmployee(employee);
                      }}
                      className={`${
                        employee.isAbsent
                          ? "bg-red-100 text-red-700"
                          : employee.isLeave || employee.isAbsent
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-gray-100 text-gray-700 cursor-pointer"
                      } px-4 py-3 w-[200px] rounded-lg text-sm font-medium whitespace-nowrap`}
                    >
                      A | {employee.isAbsent ? "Absent" : "Not Absent"}
                    </div>

                    {/* Row 2 */}
                    <div
                      onClick={() => {
                        if (employee.isLeave || employee.isAbsent) return;
                        setIsFine(true);
                        setSelectedEmployee(employee);
                        setFineAmount(employee.fine?.amount || "");
                      }}
                      className={`${
                        employee.fine?.amount
                          ? "bg-green-100 text-green-800"
                          : employee.isLeave || employee.isAbsent
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-gray-100 text-gray-700 cursor-pointer"
                      } px-4 py-3 w-[200px] rounded-lg text-sm font-medium whitespace-nowrap`}
                    >
                      F |{" "}
                      {employee.fine?.amount
                        ? `₹${employee.fine.amount}`
                        : "No Fine"}
                    </div>
                    <div
                      onClick={() => {
                        if (
                          employee.isLeave ||
                          employee.isAbsent ||
                          !employee.loginTime
                        )
                          return;
                        setIsOvertime(true);
                        setOvertimeLoginTime(employee?.overTimeDetail?.login);
                        setOvertimeLogoutTime(employee?.overTimeDetail?.logout);
                        setSelectedEmployee(employee);
                      }}
                      className={`${
                        employee.overTime
                          ? "bg-blue-100 text-blue-800"
                          : employee.isLeave ||
                            employee.isAbsent ||
                            !employee.loginTime
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-gray-100 text-gray-700 cursor-pointer"
                      } px-4 py-3 w-[200px] rounded-lg text-sm font-medium whitespace-nowrap`}
                    >
                      OT | {employee.overTime || "No Overtime"}
                    </div>
                    <div
                      onClick={() => {
                        if (employee.isLeave || employee.isAbsent) return;
                        setIsLeave(true);
                        setSelectedEmployee(employee);
                      }}
                      className={`${
                        employee.isLeave
                          ? "bg-purple-100 text-purple-800"
                          : employee.isLeave || employee.isAbsent
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-gray-100 text-gray-700 cursor-pointer"
                      } px-4 py-3 w-[200px] rounded-lg text-sm font-medium whitespace-nowrap`}
                    >
                      L | {employee.isLeave ? "Leave" : "No Leave"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3  mt-4">
                {employee?.employeeActivity?.loginImageUrl ? (
                    <img
                      src={employee?.employeeActivity?.loginImageUrl}
                      alt="login"
                      label="Check In Image"
                    
                      className="h-10 w-10 object-cover rounded-md cursor-pointer hover:scale-105 transition-transform"
                      onClick={() => handleImageClick(
                        employee?.employeeActivity?.loginImageUrl,
                        employee?.employeeActivity?.loginLatitude,
                        employee?.employeeActivity?.loginLongitude,
                        "checkIn"
                      )}
                    />
                  ) : (
                    <div className="h-10 w-10 flex items-center justify-center bg-gray-200 text-gray-500 rounded-md">
                      N/A
                    </div>
                  )}
                  <span
                    className={`px-3 py-1 text-nowrap rounded-full border text-xs font-medium ${
                      employee?.employeeActivity?.loginImageUrl
                        ? "bg-blue-100 text-blue-800 border-blue-800"
                        : "bg-gray-100 text-gray-500 border-gray-300"
                    }`}
                  >
                    {employee?.employeeActivity?.loginImageUrl ? "Check In" : "Not Checked In"}
                  </span>
                  {employee?.employeeActivity?.logoutImageUrl ? (
                    <img
                      src={employee?.employeeActivity?.logoutImageUrl}
                      alt="logout"
                      className="h-10 w-10 object-cover rounded-md cursor-pointer hover:scale-105 transition-transform"
                      onClick={() => handleImageClick(
                        employee?.employeeActivity?.logoutImageUrl,
                        employee?.employeeActivity?.logoutLatitude,
                        employee?.employeeActivity?.logoutLongitude,
                        "checkOut"
                      )}
                    />
                  ) : (
                    <div className="h-10 w-10 flex items-center justify-center bg-gray-200 text-gray-500 rounded-md">
                      N/A
                    </div>
                  )}
                  <span
                    className={`px-3 py-1 text-nowrap rounded-full border text-xs font-medium ${
                      employee?.employeeActivity?.logoutImageUrl
                        ? "bg-red-100 text-red-800 border-red-800"
                        : "bg-gray-100 text-gray-500 border-gray-300"
                    }`}
                  >
                    {employee?.employeeActivity?.logoutImageUrl ? "Check Out" : "Not Checked Out"}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center bg-white rounded-md text-gray-500 py-10 flex flex-col items-center justify-center">
              {/* Illustration from undraw */}
              <img
                src="https://cdni.iconscout.com/illustration/premium/thumb/empty-wishlist-illustration-svg-png-download-9824474.png"
                alt="empty"
                className="w-52 h-52 mb-4"
              />
              <p className="text-lg font-medium">No Employees Found</p>
              <p className="text-sm text-gray-400">
                Your employee list is empty.
              </p>
            </div>
          )}
        </div>

        {/* Sentinel element for IntersectionObserver */}
        {hasMore && (
          <div ref={observerRef} className="h-10">
            {isLoading && (
              <div className="text-center py-4 text-gray-500">
                <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-4 border-customGreen"></div>
              </div>
            )}
          </div>
        )}
      </div>

      <CustomModel
        isOpen={isImageModalVisible}
        width="w-[950px]"
        isClose={() => {
          setIsImageModalVisible(false);
          setSelectedImage("");
          setSelectedCoordinates(null);
          setCurrentLocationAddress("Loading...");
          setImageLoading(false);
        }}
        title={`${selectedType === "checkIn" ? "Check In" : "Check Out"} Location Details`}
        content={
          <div className="p-">
            {selectedImage ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                

                {/*Left  Side - Map */}
                <div className="flex flex-col gap-4">
                  <h3 className="text-lg font-semibold text-gray-800"> Map View</h3>
                  <div className="w-full h-[400px] rounded-lg overflow-hidden border border-gray-300 shadow-sm">
                    {!isLoaded ? (
                      <div className="flex items-center justify-center h-full bg-gray-100">
                        <div className="flex flex-col items-center gap-2">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          <span className="text-sm text-gray-600">Loading map...</span>
                        </div>
                      </div>
                    ) : selectedCoordinates ? (
                      <GoogleMap
                        mapContainerStyle={{ width: "100%", height: "100%" }}
                        center={{
                          lat: parseFloat(selectedCoordinates.split(",")[0]),
                          lng: parseFloat(selectedCoordinates.split(",")[1]),
                        }}
                        zoom={16}
                        options={{
                          styles: [
                            {
                              featureType: "poi",
                              elementType: "labels",
                              stylers: [{ visibility: "off" }]
                            }
                          ]
                        }}
                      >
                        <Marker
                          position={{
                            lat: parseFloat(selectedCoordinates.split(",")[0]),
                            lng: parseFloat(selectedCoordinates.split(",")[1]),
                          }}
                          icon={{
                            url: selectedType === "checkIn" 
                              ? "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(`
                                  <svg width="32" height="40" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M16 0C7.163 0 0 7.163 0 16c0 16 16 24 16 24s16-8 16-24C32 7.163 24.837 0 16 0z" fill="#2563eb"/>
                                    <circle cx="16" cy="16" r="8" fill="#ffffff"/>
                                    <text x="16" y="21" text-anchor="middle" font-family="Arial" font-size="10" font-weight="bold" fill="#2563eb">IN</text>
                                  </svg>
                                `)
                              : "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(`
                                  <svg width="32" height="40" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M16 0C7.163 0 0 7.163 0 16c0 16 16 24 16 24s16-8 16-24C32 7.163 24.837 0 16 0z" fill="#dc2626"/>
                                    <circle cx="16" cy="16" r="8" fill="#ffffff"/>
                                    <text x="16" y="21" text-anchor="middle" font-family="Arial" font-size="9" font-weight="bold" fill="#dc2626">OUT</text>
                                  </svg>
                                `),
                            scaledSize: new window.google.maps.Size(32, 40),
                            anchor: new window.google.maps.Point(16, 40),
                          }}
                        />
                      </GoogleMap>
                    ) : (
                      <div className="flex items-center justify-center h-full bg-gray-100">
                        <p className="text-gray-500">No location data available</p>
                      </div>
                    )}
                  </div>
                  
                    {/* Location Info */}
                    <div className="bg-gray-50 p-4 rounded-lg border">
                    <h4 className="font-semibold text-gray-700 mb-2">📍 Address Information</h4>
                    <div className="text-sm text-gray-600">
                      {currentLocationAddress === "Loading..." ? (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          <span>Loading address...</span>
                        </div>
                      ) : (
                        <p>{currentLocationAddress}</p>
                      )}
                    </div>
                    {selectedCoordinates && (
                      <div className="mt-2 text-xs text-gray-500">
                        <span className="font-medium">Coordinates:</span> {selectedCoordinates}
                      </div>
                    )}
                  </div>
                </div>
                {/* Right Side - Image */}
                <div className="flex flex-col gap-4">
                  <h3 className="text-lg font-semibold text-gray-800">Location Image</h3>
                  <div className="relative">
                    {imageLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-lg z-10">
                        <div className="flex flex-col items-center gap-2">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          <span className="text-sm text-gray-600">Loading image...</span>
                        </div>
                      </div>
                    )}
                    <img
                      src={selectedImage}
                      alt="Location"
                      className="w-full h-[550px] object-cover rounded-lg border border-gray-300 shadow-sm"
                      onLoad={() => setImageLoading(false)}
                      onLoadStart={() => setImageLoading(true)}
                      onError={() => setImageLoading(false)}
                    />
                  </div>
                  
               
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
               
                <p className="text-gray-500 text-lg">No image available</p>
                <p className="text-gray-400 text-sm mt-2">No location image was captured for this activity</p>
              </div>
            )}
          </div>
        }
      />

      {/* Custom Models */}
      <CustomModel
        title="Add Log Notes"
        isOpen={isNotesOpen}
        loading={loading || !notes}
        isClose={() => setIsNotesOpen(false)}
        onConfirmText="Save"
        onConfirm={handleManageNotes}
        content={
          <div className="">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full h-32 p-2 border border-gray-300 rounded-lg input-focus"
              placeholder="Add your notes here..."
              maxLength={250}
            ></textarea>
          </div>
        }
      />

      <CustomModel
        title="Leave Request"
        isOpen={isLeave}
        loading={loading || !reason}
        isClose={() => setIsLeave(false)}
        onConfirmText="Save"
        onConfirm={handleManageLeave}
        content={
          <div className="">
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full h-32 p-2 border border-gray-300 rounded-lg input-focus"
              placeholder="Add your notes here..."
              maxLength={250}
            ></textarea>
          </div>
        }
      />

      <CustomModel
        title="Present"
        isOpen={isPresent}
        isClose={() => setIsPresent(false)}
        onConfirmText="Save"
        loading={loading}
        onConfirm={handleManagePresent}
        content={
          <div className="flex flex-col gap-4">
            <div className="flex flex-col text-customGreen mb-5 gap-2">
              <div className="flex text-customGreen items-center gap-2">
                <label htmlFor="employee-name" className="font-medium">
                  Employee Name:
                </label>
                <div className="font-medium">{selectedEmployee?.userName}</div>
              </div>
              <div className="flex text-customGreen items-center gap-2">
                <label htmlFor="employee-name" className="font-medium">
                  Date:
                </label>
                <div className="font-medium">
                  {formatToIndianDate(selectedDate)}
                </div>
              </div>
            </div>
            <div className="flex w-full items-center gap-2 my-4">
              <div className="flex flex-col w-full gap-2">
                <label htmlFor="login-time">Login Time:</label>
                <input
                  type="time"
                  id="login-time"
                  name="login-time"
                  className="p-3 w-full input-focus"
                  value={loginTime}
                  onChange={(e) => setLoginTime(e.target.value)}
                />
              </div>
              {isLoginTime && (
                <div className="flex flex-col w-full gap-2">
                  <label htmlFor="logout-time">Logout Time:</label>
                  <input
                    type="time"
                    id="logout-time"
                    name="logout-time"
                    className="p-3 w-full input-focus"
                    value={logoutTime}
                    onChange={(e) => setLogoutTime(e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>
        }
      />

      <CustomModel
        title="Over Time"
        isOpen={isOvertime}
        isClose={() => setIsOvertime(false)}
        onConfirmText="Save"
        loading={loading}
        onConfirm={handleManageOvertime}
        content={
          <div className="flex flex-col gap-4">
            <div className="flex flex-col text-customGreen mb-5 gap-2">
              <div className="flex text-customGreen items-center gap-2">
                <label htmlFor="employee-name" className="font-medium">
                  Employee Name:
                </label>
                <div className="font-medium">{selectedEmployee?.userName}</div>
              </div>
              <div className="flex text-customGreen items-center gap-2">
                <label htmlFor="employee-name" className="font-medium">
                  Date:
                </label>
                <div className="font-medium">
                  {formatToIndianDate(selectedDate)}
                </div>
              </div>
            </div>
            <div className="flex w-full items-center gap-2 my-4">
              <div className="flex flex-col w-full gap-2">
                <label htmlFor="login-time">Login Time:</label>
                <input
                  type="time"
                  id="login-time"
                  name="login-time"
                  className="p-3 w-full input-focus"
                  value={overtimeLoginTime}
                  onChange={(e) => setOvertimeLoginTime(e.target.value)}
                />
              </div>
              <div className="flex flex-col w-full gap-2">
                <label htmlFor="logout-time">Logout Time:</label>
                <input
                  type="time"
                  id="logout-time"
                  name="logout-time"
                  className="p-3 w-full input-focus"
                  value={overtimeLogoutTime}
                  onChange={(e) => setOvertimeLogoutTime(e.target.value)}
                />
              </div>
            </div>
          </div>
        }
      />

      <CustomModel
        title="Fine"
        isOpen={isFine}
        loading={loading}
        isClose={() => setIsFine(false)}
        onConfirmText="Save"
        onConfirm={handleManageFine}
        content={
          <div className="flex flex-col gap-4">
            <div className="flex flex-col text-customGreen mb-5 gap-2">
              <div className="flex text-customGreen items-center gap-2">
                <label htmlFor="employee-name" className="font-medium">
                  Employee Name:
                </label>
                <div className="font-medium">{selectedEmployee?.userName}</div>
              </div>
              <div className="flex text-customGreen items-center gap-2">
                <label htmlFor="employee-name" className="font-medium">
                  Date:
                </label>
                <div className="font-medium">
                  {formatToIndianDate(selectedDate)}
                </div>
              </div>
            </div>
            <div className="flex w-full items-center gap-2 my-4">
              <div className="flex flex-col w-full gap-2">
                <label htmlFor="fine-amount">Fine Amount:</label>
                <input
                  type="text"
                  id="fine-amount"
                  name="fine-amount"
                  className="p-3 w-full input-focus"
                  value={fineAmount}
                  onChange={(e) => setFineAmount(e.target.value)}
                />
              </div>
            </div>
          </div>
        }
      />

      <CustomModel
        title="Half Day"
        isOpen={isHalfDay}
        loading={loading}
        isClose={() => setIsHalfDay(false)}
        onConfirmText="Mark as Half Day"
        onConfirm={handleManageHalfDay}
        content={
          <div className="flex flex-col gap-4">
            <div className="flex flex-col text-customGreen mb-5 gap-2">
              <div className="flex text-customGreen items-center gap-2">
                <label htmlFor="employee-name" className="font-medium">
                  Employee Name:
                </label>
                <div className="font-medium">{selectedEmployee?.userName}</div>
              </div>
              <div className="flex text-customGreen items-center gap-2">
                <label htmlFor="employee-name" className="font-medium">
                  Date:
                </label>
                <div className="font-medium">
                  {formatToIndianDate(selectedDate)}
                </div>
              </div>
            </div>
            <div className="flex w-full items-center gap-2 my-4">
              <div className="flex flex-col w-full gap-2">
                <label htmlFor="logout-time">Logout Time:</label>
                <input
                  type="time"
                  id="logout-time"
                  name="logout-time"
                  className="p-3 w-full input-focus"
                  value={halfDayTime}
                  onChange={(e) => setHalfDayTime(e.target.value)}
                />
              </div>
            </div>
          </div>
        }
      />

      <CustomModel
        title="Absent"
        isOpen={isAbsent}
        isClose={() => setIsAbsent(false)}
        loading={loading}
        onConfirmText="Mark as Absent"
        onConfirm={handleManageAbsent}
        content={
          <div className="flex flex-col gap-4">
            <div className="flex flex-col text-customGreen mb-5 gap-2">
              <div className="flex text-customGreen items-center gap-2">
                <label htmlFor="employee-name" className="font-medium">
                  Employee Name:
                </label>
                <div className="font-medium">
                  {selectedEmployee?.user?.label}
                </div>
              </div>
              <div className="flex text-customGreen items-center gap-2">
                <label htmlFor="employee-name" className="font-medium">
                  Date:
                </label>
                <div className="font-medium">
                  {formatToIndianDate(selectedDate)}
                </div>
              </div>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default AttendanceEmployeeList;
